import type { Vec3 } from './assemblyPose';
import { FACES as FACE_ORDER, FACE_DIR, OPPOSITE, type Face } from './faces';

// HOW BIG A PART IS, in millimetres along each axis — as much of a size record as anything
// here reads. Written as a shape rather than taken from either of the two types that have it
// (Dims in the viewer, PartSize in the parts list): both are that shape plus fields about
// fasteners, and this module has no business knowing which of them it was handed.
export type Extents = { x?: number | null; y?: number | null; z?: number | null } | null | undefined;

// WHERE TWO PARTS TOUCH, SAID AS A PLACE ON EACH OF THEM.
//
// PHASE 5 USED TO ASK FOR SIX NUMBERS: where the moving part comes to rest and how it is
// turned, in centimetres and degrees, in a frame the model has never seen. It answered them
// the only way it could — by eye, off two crops at unknown scale — and its own reasoning says
// so: "I estimate x ≈ −18 to −20cm". What came back was a side rail resting 40 mm from the
// frame it is described as being held against, on a sub-step whose whole content is that they
// touch.
//
// The distance was never the model's to invent. "Against" is a constraint: the two parts meet.
// What a model reading a diagram genuinely knows is WHERE they meet — which face of the frame
// the rail lands on, and whereabouts on it — and that is a question about the pictures in
// front of it rather than about a coordinate system it has to imagine.
//
// So the answer is a CONTACT: one face and one point per part, and how far out the moving part
// starts. Everything else is arithmetic. The parts touch because the numbers are computed from
// the requirement that they touch, not because somebody checked afterwards that they did.
//
// AND THE ROTATION COMES FREE, which is the part that makes this work at all. mesh_orient
// re-authors every reconstruction into the FINISHED PRODUCT's frame — it renders the mesh down
// its six axes, matches them against photographs of the assembled item, and turns the file — so
// a part's "front" is the product's front and its +z is the product's up. Two parts already
// standing the right way round need no orientation answered between them: naming the faces
// names the rotation, and all that is left is the three translations.
//
// WHICH MAKES THE PART THAT HAS NOT BEEN ORIENTED the one case this cannot save. It will be
// seated touching and lying on its side, and the fix for that is in Phase 2 where the
// orientation is answered, not here.

// THE SIX FACES, each as the axis it faces along and which end of that axis it sits at —
// DERIVED from the one place that names them (see faces.ts, which argues why there is one
// place). The shape is what the box drawn on a mesh wants; the fact is FACE_DIR's.
export const FACES = FACE_ORDER.map((word) => {
  const dir = FACE_DIR[word];
  const at = dir[0] + dir[1] + dir[2] > 0 ? 'max' : 'min';
  return { axis: (['x', 'y', 'z'] as const)[dir.findIndex((c) => c !== 0)], at, word };
});

export type FaceWord = Face;

export const oppositeFace = (face: FaceWord): FaceWord => OPPOSITE[face];

// A PLACE ON ONE FACE: millimetres from the corner, across and up.
//
// MILLIMETRES AND NOT PERCENTAGES, though percentages were the obvious form for it — one unit
// is better than two, the model is given the face's own size in the prompt, and one percent of
// a 480 mm frame is five millimetres, which is the whole tolerance of the thing being placed.
export type FacePoint = { face: FaceWord; u: number; v: number };

const AXIS_INDEX = { x: 0, y: 1, z: 2 } as const;

const cross = (a: Vec3, b: Vec3): Vec3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];

// WHICH WAY THE FACE LOOKS: out of the part, in the part's own frame — which is the product's
// frame, every mesh having been authored into it.
export const faceNormal = (face: FaceWord): Vec3 => FACE_DIR[face];

// AND WHICH WAY ACROSS IT `u` AND `v` RUN.
//
// THE CONVENTION, ONCE: you are looking at the face from OUTSIDE the part, along −normal. The
// origin is the bottom-left corner of what you see, `u` runs left to right and `v` runs bottom
// to top. For the four upright faces "up" is the product's up, which settles both.
//
// THE TOP AND THE BOTTOM HAVE NO UP — the normal is the up — so the rule is stated rather than
// derived: `v` runs toward the product's FRONT, and `u` follows from it like every other face.
// Without that sentence a model picks a different corner every time it is asked, and so does
// whoever reads the answer.
//
// `u = v × n` is what "left to right seen from outside" means as arithmetic. It is worth
// checking once by hand: on the front face (n = −y, v = +z) it gives +x, and standing in front
// of a chair looking at it, the product's +x IS to your right.
export const faceBasis = (face: FaceWord): { n: Vec3; u: Vec3; v: Vec3 } => {
  const n = faceNormal(face);
  const v: Vec3 = face === 'top' || face === 'bottom' ? [0, -1, 0] : [0, 0, 1];
  return { n, u: cross(v, n), v };
};

// HOW BIG A PART IS ALONG EACH AXIS, in millimetres — the extents the mesh is drawn at, which
// is what the (u, v) on its faces are measured across. Null where the part has never been
// measured: a screw arrives with one length and no axes (see Dims), and a contact on a face
// whose size nobody knows cannot be turned into a place.
const extentsOf = (dims: Extents): Vec3 | null => {
  const { x, y, z } = dims ?? {};
  return typeof x === 'number' && typeof y === 'number' && typeof z === 'number'
    ? [x, y, z]
    : null;
};

// The length of a part along a direction, which for an axis-aligned unit vector is one of its
// three extents. Written as a dot product of absolutes so it holds for either sign.
const along = (size: Vec3, dir: Vec3): number =>
  Math.abs(dir[0]) * size[0] + Math.abs(dir[1]) * size[1] + Math.abs(dir[2]) * size[2];

// THE RECTANGLE A FACE PRESENTS, in the two directions the point is measured along — for the
// prompt, which has to tell the model what range its two numbers may take and which way each
// of them runs. A face is the part's bounding box's face: a reconstruction is a curved,
// organic solid with no faces in the CAD sense, and "60% along the inner face" of one means
// nothing until it is a spot on a rectangle.
// Which of the six words a direction points at, so a place on a face can be said as "0 at the
// back edge to 135 at the front" instead of by naming an axis nobody can see.
const wordFor = (dir: Vec3): FaceWord => FACES.find(
  (f) => dir[AXIS_INDEX[f.axis]] === (f.at === 'max' ? 1 : -1),
)!.word;

// WHICH WAY THE TWO MEASUREMENTS ON A FACE RUN, and therefore which edges they are counted
// FROM — a property of the face alone, with no size in it.
//
// `uTo` and `vTo` are the edges each runs toward, so their opposites are the origins: u is 0
// at `oppositeFace(uTo)`. Which is the whole of what a reader needs to check a resolved
// number, and it changes per face — it falls out of u = v × n — so "358" on the right face is
// counted from the front and on the left face from the back.
//
// Split out of faceRects because that one needs three measured axes to answer at all, and
// this question has no size in it: a part nobody has measured still has six faces that run
// the way they run.
export const faceAxes = (face: FaceWord): { uTo: FaceWord; vTo: FaceWord } => {
  const { u, v } = faceBasis(face);
  return { uTo: wordFor(u), vTo: wordFor(v) };
};

// WHICH OF THE PRODUCT'S THREE AXES EACH OF A FACE'S TWO DIRECTIONS RUNS ALONG.
//
// A face is a rectangle and its two directions are always two of width, depth and height —
// never a third thing, and never in doubt once the face is named. Which is what makes them
// worth SAYING: "u" and "v" are slots whose meaning changes with the face, so a figure written
// into the wrong one is a perfectly legal record that nothing downstream can question, while
// "height: 35 mm from the back" contradicts itself in the words themselves.
//
// x across, y the depth, z up — the frame FACE_DIR is built in, named as the viewer labels its
// gizmo and as the holes table prints its POSITION column, so one vocabulary reaches the model,
// the reader and the picture.
export type SizeAxis = 'width' | 'depth' | 'height';

const AXIS_WORDS: SizeAxis[] = ['width', 'depth', 'height'];

export const faceSizeAxes = (face: FaceWord): { u: SizeAxis; v: SizeAxis } => {
  const { u, v } = faceBasis(face);
  const of = (a: Vec3): SizeAxis => AXIS_WORDS[a.findIndex((c) => c !== 0)];
  return { u: of(u), v: of(v) };
};

export function faceRects(dims: Extents): {
  face: FaceWord; u: number; v: number; uTo: FaceWord; vTo: FaceWord;
  uAxis: SizeAxis; vAxis: SizeAxis;
}[] | null {
  const size = extentsOf(dims);
  if (!size) return null;
  return FACES.map(({ word }) => {
    const { u, v } = faceBasis(word);
    const axes = faceSizeAxes(word);
    return {
      face: word,
      u: Math.round(along(size, u)),
      v: Math.round(along(size, v)),
      uAxis: axes.u,
      vAxis: axes.v,
      ...faceAxes(word),
    };
  });
}

// WHERE A POINT ON A FACE STANDS IN THE PART'S OWN FRAME, in millimetres.
//
// MEASURED FROM THE BOUNDING BOX'S CENTRE, because that is where every mesh in this phase is
// re-origined when it loads (see assemblyScene) and therefore what every position in a fitting
// is about. So the point is: out to the face along its normal, then across from the corner.
//
// Clamped to the face, since a model that answers 500 on a 482 mm edge has said "the far end"
// and not "somewhere off the part".
export function pointOnFace(at: FacePoint, dims: Extents): Vec3 | null {
  const size = extentsOf(dims);
  if (!size) return null;
  const { n, u, v } = faceBasis(at.face);
  const halfU = along(size, u) / 2;
  const halfV = along(size, v) / 2;
  const du = Math.min(halfU * 2, Math.max(0, at.u)) - halfU;
  const dv = Math.min(halfV * 2, Math.max(0, at.v)) - halfV;
  const out = along(size, n) / 2;
  const place = (i: number) => n[i] * out + u[i] * du + v[i] * dv;
  return [place(0), place(1), place(2)];
}
